"use client";

import { Box, Button, Stack, Text, UnstyledButton } from "@mantine/core";
import type { ProjectMembership } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react-hooks";
import { useMemo } from "react";
import { buildProfileOptions, type ProfileOption } from "@/lib/auth/profile-selection";
import styles from "./ProfileSelection.module.scss";

interface ProfileSelectionProps {
  memberships: ProjectMembership[];
  isSubmitting?: boolean;
  onSelect: (membership: ProjectMembership) => Promise<void> | void;
  onBack?: () => void;
  title?: string;
  subtitle?: string;
}

export function ProfileSelection({
  memberships,
  isSubmitting = false,
  onSelect,
  onBack,
  title = "Select a profile",
  subtitle = "Choose the account you want to use.",
}: ProfileSelectionProps) {
  //------------------------------------------------------------------------------------------------
  //                                       DEPENDENCIES
  //------------------------------------------------------------------------------------------------
  const medplum = useMedplum();

  //------------------------------------------------------------------------------------------------
  //                                          STATE
  //------------------------------------------------------------------------------------------------
  const options = useMemo(
    () => buildProfileOptions({ medplum, memberships }),
    [medplum, memberships],
  );

  const groupedOptions = useMemo(() => {
    const grouped = new Map<string, ProfileOption[]>();
    for (const option of options) {
      const group = option.typeLabel;
      if (!grouped.has(group)) {
        grouped.set(group, []);
      }
      grouped.get(group)?.push(option);
    }
    return Array.from(grouped.entries());
  }, [options]);

  //------------------------------------------------------------------------------------------------
  //                                         FUNCTIONS
  //------------------------------------------------------------------------------------------------
  /**
   * Handles a profile selection and delegates to the parent.
   */
  function handleSelect(option: ProfileOption) {
    onSelect(option.membership);
  }

  //------------------------------------------------------------------------------------------------
  //                                          RENDER
  //------------------------------------------------------------------------------------------------
  return (
    <Stack className={styles.Container} gap="xl">
      <Stack gap="xxs">
        <Text className={styles.Title}>{title}</Text>
        <Text className={styles.Subtitle}>{subtitle}</Text>
      </Stack>

      {groupedOptions.length === 0 && (
        <Text size="sm" c="dimmed">
          No profiles are available for this account.
        </Text>
      )}

      {groupedOptions.map(([group, groupOptions]) => (
        <Stack key={group} gap="xs" className={styles.Group}>
          <Text className={styles.GroupLabel}>{group}</Text>
          <Stack gap="xs">
            {groupOptions.map((option) => (
              <UnstyledButton
                key={
                  option.membership.id ??
                  option.membership.profile?.reference ??
                  `${group}-${option.displayName}`
                }
                className={styles.OptionCard}
                onClick={() => handleSelect(option)}
                disabled={isSubmitting}
                data-disabled={isSubmitting || undefined}
              >
                <Box className={styles.OptionContent}>
                  <Text className={styles.OptionTitle}>{option.displayName}</Text>
                  {option.practiceLabel && (
                    <Text className={styles.OptionMeta}>{option.practiceLabel}</Text>
                  )}
                  {option.identifierLabel && (
                    <Text className={styles.OptionMeta}>{option.identifierLabel}</Text>
                  )}
                </Box>
                <span className={styles.OptionChevron}>&#8250;</span>
              </UnstyledButton>
            ))}
          </Stack>
        </Stack>
      ))}

      {onBack && (
        <Button variant="subtle" size="compact-sm" onClick={onBack} disabled={isSubmitting}>
          Back to sign in
        </Button>
      )}
    </Stack>
  );
}
